<template>
  <div class="mobile-menu" :class="{ active: active }">

    <el-menu class="mobile-menu__main" mode="vertical" :router="true" :default-active="$route.path">
      <nav-mobile-inline-submenu v-if="isLoggedIn" :parentId="null" submenuId="products"
        :expandedSubmenu="expandedSubmenu" :expandedChanged="setExpanded">
        <template v-slot:title>
          {{ $t('menuProducts') }}
        </template>
        <template v-slot:entries>
          <nav-mobile-product-categories v-show="expandedSubmenu === 'products'" submenuId="products"
            :expandedSubmenu="expandedSubmenu" :expandedChanged="setExpanded"></nav-mobile-product-categories>
        </template>
      </nav-mobile-inline-submenu>
      <el-menu-item v-if="supportedFeaturesTraining && expandedSubmenu === null" index="training"
        :route="{ name: 'Training' }">{{
          $t('menuTraining')
        }}
      </el-menu-item>
      <nav-mobile-inline-submenu v-if="isLoggedIn" :parentId="null" submenuId="usefulResources"
        :expandedSubmenu="expandedSubmenu" :expandedChanged="setExpanded">
        <template v-slot:title>
          {{ $t('menuResources') }}
        </template>
        <template v-slot:entries>
          <el-menu-item v-show="expandedSubmenu === 'usefulResources'" v-for="(item, key) in usefulResourceLinks"
            :key="key">
            <a :href="item.url" target="_blank" @click="closeNavigation">{{ item.title }}</a>
          </el-menu-item>
        </template>
      </nav-mobile-inline-submenu>
    </el-menu>

    <nav-mobile-utility></nav-mobile-utility>

  </div>
</template>

<script>
import authentication from '@/authentication';
import mixins from '@/mixins';
import navigation from '@/components/navigation';
import offline from '@/offline';
import products from '@/components/products';
import user from '@/components/user';

import NavMobileInlineSubmenu from './NavMobileInlineSubmenu.vue';
import NavMobileProductCategories from './NavMobileProductCategories.vue';
import NavMobileUtility from './NavMobileUtility.vue';
import { mapGetters, mapState } from 'vuex';

export default {
  name: 'NavMobile',
  data: () => {
    return {
      expandedSubmenu: null,
    };
  },
  mixins: [
    mixins.supportedFeatures
  ],
  props: {
    active: {
      type: Boolean,
      required: true,
    },
    closeNavigation: {
      type: Function,
      required: true,
    }
  },
  watch: {
    active(newActive, oldActive) {
      if (oldActive && !newActive) {
        // This is slightly larger than the transition duration
        // to ensure that the transition completes before we revert state
        setTimeout(() => this.expandedSubmenu = null, 225);
      }
    },
  },
  computed: {
    ...mapGetters(user.store.namespace, [
      'region'
    ]),
    ...mapState(authentication.store.namespace, [
      'isLoggedIn'
    ]),
    ...mapState(offline.store.namespace, [
      'isOnline'
    ]),
    ...mapState(products.store.namespace, [
      'categories'
    ]),
    ...mapState(navigation.store.namespace, [
      'utilityLinks',
      'usefulResourceLinks',
    ]),
  },
  methods: {
    shouldShow(slug) {
      return this.expandedSubmenu === null || this.expandedSubmenu === slug;
    },
    setExpanded(slug) {
      this.expandedSubmenu = slug;
    }
  },
  components: {
    NavMobileInlineSubmenu,
    NavMobileProductCategories,
    NavMobileUtility,
  },
}
</script>

<style lang="scss" scoped>
@import '@/assets/base/_variables.scss';

.mobile-menu {
  position: fixed;
  z-index: 10000;
  top: $--menu-height-mobile;
  left: 100vw;
  right: 0;
  bottom: 0;
  overflow: auto;
  -webkit-overflow-scrolling: touch;
  background: $--color-white;
  font-family: $font-family-headings;
  transition: left 200ms, right 200ms;

  &.active {
    left: 0;
    right: 0;
    transition: left 200ms, right 200ms;
  }

  &:deep(.el-menu-item),
  &:deep(.el-menu-item)>a,
  &:deep(.el-menu-item)>span,
  &:deep(.el-menu-item)>.el-input {
    font-family: $font-family-headings;
    font-weight: bold;
    font-size: 14px;
    text-transform: uppercase;
  }

  &>.mobile-menu__main {
    text-align: left;

    &:deep(.el-menu-item) {
      border-bottom: solid 1px $--color-grayscale-2;

      &.nested {
        // Important necessary because el-menu gets a "style" tag applied
        padding-left: 50px !important;
      }
    }

    &:deep(.el-menu-item),
    &:deep(.el-menu-item)>a,
    &:deep(.el-menu-item)>span {
      font-size: 20px;
      color: $--color-secondary;
    }
  }

  &>ul.mobile-menu__utility.el-menu {
    background-color: $--color-black;
    padding-bottom: 1rem;

    &:deep(.el-menu-item),
    &:deep(.el-menu-item)>a,
    &:deep(.el-menu-item)>span {
      color: $--color-white;
    }

    &>.el-menu-item {
      margin-left: 1rem;
      margin-right: 1rem;
      padding-left: 0.25rem !important;
      padding-right: 0.25rem !important;
      border-bottom: solid 1px $--color-white;

      &.search {
        display: block;
        padding: 0 !important;
        border-bottom: none;
        background-color: $--color-black;

        &.is-active {
          background-color: $--color-black;
        }

        &:deep(i) {
          padding-top: 2px;
          font-size: 18px;
          color: $--color-primary;
        }

        &:deep(input) {
          margin-top: -5px;
          height: 57px;
          font-family: $font-family-headings;
          font-weight: bold;
          font-size: 14px;
          text-transform: uppercase;
          color: $--color-secondary;

          &::placeholder {
            color: $--color-menu-gray;
          }

          &:focus {
            border-color: $--color-primary;
            outline: 1px solid $--color-primary;
          }
        }
      }
    }
  }

  .el-menu--horizontal,
  .el-menu--horizontal>.el-menu-item {
    border: none;
  }
}
</style>
